import { DispatchAction } from "@iolabs/redux-utils";
import { Box, CircularProgress, IconButton, InputAdornment, TextField } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { IAttachedEquipment, IElementsPosition } from "../../../packages/Api/data/elements/types";
import {
    onDeleteAttachedEquipment,
    onUpdateAttachedEquipment,
    onEquipmentCatalogue,
    useDetailPosition,
    useMappingAttachedEquipment,
    useMappingEquipmentLoading,
} from "../../../redux/mapping";
import { useActiveProject } from "../../../redux/project";
import { formatNumber } from "../../../utils/Formatter";
import Icon from "../../Icon/Icon";
import { ProjectData } from "../../ProjectSelectorWrapper/type";
import useStyles from "./styles";
import { useIntl } from "react-intl";
import messages from "./messages";
import SecuredComponent from "../../../redux/keyclock/SecuredComponent/SecuredComponent";

export enum UpdatableField {
    kMenge = "kMenge",
    liPrice = "liPrice",
}

interface IAttachedEquipmentUpdate extends IAttachedEquipment {
    isUpdateMode?: {
        [key: string]: boolean;
    };
}

interface IStepEquipmentFormEquipmentProps {}

const StepEquipmentFormEquipment: React.FC<IStepEquipmentFormEquipmentProps> = () => {
    const classes = useStyles();
    const dispatch = useDispatch<DispatchAction>();
    const activeProject: ProjectData | undefined = useActiveProject();
    const detailPosition: IElementsPosition | undefined = useDetailPosition();

    const mappingEquipmentLoading: boolean = useMappingEquipmentLoading();
    const mappingAttachedEquipment: IAttachedEquipment[] = useMappingAttachedEquipment();

    const [updateAttachedEquipment, setUpdateAttachedEquipment] = useState<IAttachedEquipmentUpdate[]>([]);
    const [updateAttachedEquipmentID, setUpdateAttachedEquipmentID] = useState<number | null>(null);
    const kMengeRefs = useRef(new WeakMap());
    const liPriceRefs = useRef(new WeakMap());

    const handleToggleUpdateMode = (attachedEquipmentID: number, fieldName: UpdatableField) => {
        setUpdateAttachedEquipment((prevState) => {
            return prevState?.map((awu) => {
                return awu?.attachedEquipmentID === attachedEquipmentID && fieldName === UpdatableField.liPrice
                    ? {
                          ...awu,
                          isUpdateMode: {
                              ...awu?.isUpdateMode,
                              liPrice: !awu?.isUpdateMode?.liPrice,
                          },
                      }
                    : awu?.attachedEquipmentID === attachedEquipmentID && fieldName === UpdatableField.kMenge
                    ? {
                          ...awu,
                          isUpdateMode: {
                              ...awu?.isUpdateMode,
                              kMenge: !awu?.isUpdateMode?.kMenge,
                          },
                      }
                    : awu;
            });
        });
    };

    const handleChange = (event, row: IAttachedEquipmentUpdate) => {
        const value = parseFloat(event.target.value);
        const name = event.target.name;
        const updatedAttachedEquipment = updateAttachedEquipment?.map((awu) => {
            return awu?.attachedEquipmentID === row?.attachedEquipmentID
                ? name === "liPrice"
                    ? {
                          ...awu,
                          equipment: {
                              ...awu?.equipment,
                              [name]: value,
                          },
                      }
                    : {
                          ...awu,
                          [name]: value,
                      }
                : awu;
        });
        setUpdateAttachedEquipment(updatedAttachedEquipment);
    };

    const handleUpdateEquipment = (attachedEquipmentID: number, fieldName: UpdatableField) => {
        setUpdateAttachedEquipmentID(attachedEquipmentID);
        const updatedAttachedEquipment = updateAttachedEquipment?.find((awu) => awu?.attachedEquipmentID === attachedEquipmentID);
        if (!updatedAttachedEquipment) return;

        dispatch(
            onUpdateAttachedEquipment({
                projectID: activeProject?.projectID as number,
                positionID: detailPosition?.positionID as number,
                attachedEquipmentID: updatedAttachedEquipment?.attachedEquipmentID,
                data: {
                    liPrice: updatedAttachedEquipment?.equipment?.liPrice as number,
                    kMenge: updatedAttachedEquipment?.kMenge as number,
                },
            })
        );
        handleToggleUpdateMode(updatedAttachedEquipment?.attachedEquipmentID, fieldName);
    };

    const handleDelete = (attachedEquipmentID: number) => () => {
        if (activeProject && detailPosition) {
            dispatch(
                onDeleteAttachedEquipment({
                    projectID: activeProject?.projectID,
                    positionID: detailPosition?.positionID,
                    attachedEquipmentIDs: [attachedEquipmentID],
                })
            );
            dispatch(onEquipmentCatalogue({ }));
        }
    };

    useEffect(() => {
        setUpdateAttachedEquipment(mappingAttachedEquipment as IAttachedEquipmentUpdate[]);
        setUpdateAttachedEquipmentID(null);
    }, [mappingAttachedEquipment]);

    // translations

    const skeleton = (
        <Box className={classes.skeletonBox}>
            <Box>
                <Skeleton variant="rect" height={30} className={classes.skeleton} />
            </Box>
            <Box mt={3}>
                <Skeleton variant="rect" height={30} className={classes.skeleton} />
                <Skeleton variant="rect" height={20} width={20} className={classes.skeletonCircle} />
            </Box>
        </Box>
    );

    // translations
    const intl = useIntl();
    const transLabelItemNumber = intl.formatMessage({ ...messages.labelItemNumber });
    const transLabelProductId = intl.formatMessage({ ...messages.labelProductId });
    const transLabelListPrice = intl.formatMessage({ ...messages.labelListPrice });
    const transLabelSurcharge = intl.formatMessage({ ...messages.labelSurcharge });

    return (
        <Box>
            {mappingEquipmentLoading && updateAttachedEquipment?.length === 0 ? (
                <>{skeleton}</>
            ) : (
                <>
                    {updateAttachedEquipment?.length === 0 ? (
                        <span className={classes.empty}>Leer</span>
                    ) : (
                        <>
                            {updateAttachedEquipment?.map((awu, index) => (
                                <Box className={classes.fieldBox} key={index}>
                                    <TextField
                                        label={transLabelItemNumber}
                                        value={awu?.equipment?.itemNumber}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        color="primary"
                                        className={classes.textField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                    <TextField
                                        label={transLabelProductId}
                                        value={awu?.equipment?.productID}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        color="primary"
                                        className={classes.textField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                    <TextField
                                        label="Name" // Product name
                                        value={awu?.equipment?.productName}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        color="primary"
                                        className={classes.textField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                    <SecuredComponent permission={{resource: "mapping", scope: "mapping:update", isProject: true}}>
                                        <TextField
                                            name="kMenge"
                                            label="K.-Menge" // kMenge // todo translate de
                                            value={!awu?.isUpdateMode?.kMenge && awu?.kMenge ? formatNumber(awu?.kMenge) : awu?.kMenge}
                                            type={!awu?.isUpdateMode?.kMenge ? "text" : "number"}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            color="primary"
                                            className={classes.textField}
                                            inputRef={(input) => kMengeRefs.current.set(awu, input)}
                                            onFocus={(event) => {
                                                if (awu?.isUpdateMode?.kMenge) {
                                                    event.target.select();
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleUpdateEquipment(
                                                        awu?.attachedEquipmentID,
                                                        UpdatableField.kMenge
                                                    )
                                                }
                                            }}
                                            onChange={(event) => {
                                                if (awu?.isUpdateMode?.kMenge) {
                                                    handleChange(event, awu);
                                                }
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                readOnly: !awu?.isUpdateMode?.kMenge,
                                            }}
                                            onClick={() => {
                                                if (!awu?.isUpdateMode?.kMenge) {
                                                    // focus input on update click
                                                    kMengeRefs.current.get(awu).focus();

                                                    handleToggleUpdateMode(
                                                        awu?.attachedEquipmentID,
                                                        UpdatableField.kMenge
                                                    );
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {mappingEquipmentLoading &&
                                                        updateAttachedEquipmentID === awu?.attachedEquipmentID ? (
                                                            <CircularProgress
                                                                size={16}
                                                                className={classes.circularProgress}
                                                            />
                                                        ) : (
                                                            <>
                                                                {awu?.isUpdateMode?.kMenge ? (
                                                                    <>
                                                                        <IconButton
                                                                            aria-label="save changes"
                                                                            onClick={() =>
                                                                                handleUpdateEquipment(
                                                                                    awu?.attachedEquipmentID,
                                                                                    UpdatableField.kMenge
                                                                                )
                                                                            }
                                                                        >
                                                                            <Icon
                                                                                name="check"
                                                                                size={18}
                                                                                className={classes.iconSave}
                                                                            />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            aria-label="cancel"
                                                                            onClick={() =>
                                                                                handleToggleUpdateMode(
                                                                                    awu?.attachedEquipmentID,
                                                                                    UpdatableField.kMenge
                                                                                )
                                                                            }
                                                                        >
                                                                            <Icon
                                                                                name="close"
                                                                                size={18}
                                                                                className={classes.iconClose}
                                                                            />
                                                                        </IconButton>
                                                                    </>
                                                                ) : (
                                                                    <IconButton
                                                                        aria-label="toggle update mode"
                                                                        onClick={() => {
                                                                            // focus input on update click
                                                                            kMengeRefs.current.get(awu).focus();

                                                                            handleToggleUpdateMode(
                                                                                awu?.attachedEquipmentID,
                                                                                UpdatableField.kMenge
                                                                            );
                                                                        }}
                                                                    >
                                                                        <Icon name="pencil-solid" size={18} />
                                                                    </IconButton>
                                                                )}
                                                            </>
                                                        )}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </SecuredComponent>
                                    <SecuredComponent permission={{resource: "mapping", scope: "mapping:update", isProject: true}}>
                                        <TextField
                                            name="liPrice"
                                            label={transLabelListPrice}
                                            value={!awu?.isUpdateMode?.liPrice && awu?.equipment?.liPrice ? formatNumber(awu?.equipment?.liPrice) : awu?.equipment?.liPrice}
                                            type={!awu?.isUpdateMode?.liPrice ? "text" : "number"}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            color="primary"
                                            className={classes.textField}
                                            inputRef={(input) => liPriceRefs.current.set(awu, input)}
                                            onFocus={(event) => {
                                                if (awu?.isUpdateMode?.liPrice) {
                                                    event.target.select();
                                                }
                                            }}
                                            onChange={(event) => {
                                                if (awu?.isUpdateMode?.liPrice) {
                                                    handleChange(event, awu);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleUpdateEquipment(
                                                        awu?.attachedEquipmentID,
                                                        UpdatableField.kMenge
                                                    )
                                                }
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                readOnly: !awu?.isUpdateMode?.liPrice,
                                            }}
                                            onClick={() => {
                                                if (!awu?.isUpdateMode?.liPrice) {
                                                    // focus input on update click
                                                    liPriceRefs.current.get(awu).focus();

                                                    handleToggleUpdateMode(
                                                        awu?.attachedEquipmentID,
                                                        UpdatableField.liPrice
                                                    );
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {mappingEquipmentLoading &&
                                                        updateAttachedEquipmentID === awu?.attachedEquipmentID ? (
                                                            <CircularProgress
                                                                size={16}
                                                                className={classes.circularProgress}
                                                            />
                                                        ) : (
                                                            <>
                                                                {awu?.isUpdateMode?.liPrice ? (
                                                                    <>
                                                                        <IconButton
                                                                            aria-label="save changes"
                                                                            onClick={() =>
                                                                                handleUpdateEquipment(
                                                                                    awu?.attachedEquipmentID,
                                                                                    UpdatableField.liPrice
                                                                                )
                                                                            }
                                                                        >
                                                                            <Icon
                                                                                name="check"
                                                                                size={18}
                                                                                className={classes.iconSave}
                                                                            />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            aria-label="cancel"
                                                                            onClick={() =>
                                                                                handleToggleUpdateMode(
                                                                                    awu?.attachedEquipmentID,
                                                                                    UpdatableField.liPrice
                                                                                )
                                                                            }
                                                                        >
                                                                            <Icon
                                                                                name="close"
                                                                                size={18}
                                                                                className={classes.iconClose}
                                                                            />
                                                                        </IconButton>
                                                                    </>
                                                                ) : (
                                                                    <IconButton
                                                                        aria-label="toggle update mode"
                                                                        onClick={() => {
                                                                            // focus input on update click
                                                                            liPriceRefs.current.get(awu).focus();

                                                                            handleToggleUpdateMode(
                                                                                awu?.attachedEquipmentID,
                                                                                UpdatableField.liPrice
                                                                            );
                                                                        }}
                                                                    >
                                                                        <Icon name="pencil-solid" size={18} />
                                                                    </IconButton>
                                                                )}
                                                            </>
                                                        )}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </SecuredComponent>
                                    <TextField
                                        label="EP"
                                        value={awu?.equipment?.ep && formatNumber(awu?.equipment?.ep)}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        color="primary"
                                        className={classes.textField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {mappingEquipmentLoading &&
                                                        updateAttachedEquipmentID === awu?.attachedEquipmentID && (
                                                            <CircularProgress
                                                                size={16}
                                                                className={classes.circularProgress}
                                                            />
                                                        )}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label={transLabelSurcharge}
                                        value={awu?.equipment?.surcharge && `${formatNumber(awu?.equipment?.surcharge)} %`}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        color="primary"
                                        className={classes.textField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                    <TextField
                                        label="EP/Menge" // todo translate de
                                        value={awu?.epPerMenge && formatNumber(awu?.epPerMenge)}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        color="primary"
                                        className={classes.textField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {mappingEquipmentLoading &&
                                                        updateAttachedEquipmentID === awu?.attachedEquipmentID && (
                                                            <CircularProgress
                                                                size={16}
                                                                className={classes.circularProgress}
                                                            />
                                                        )}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box display="flex" alignItems="center">
                                        <IconButton
                                            aria-label="remove"
                                            color="secondary"
                                            className={classes.removeButton}
                                            onClick={handleDelete(awu?.attachedEquipmentID)}
                                        >
                                            <Icon name="minus" size={20} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default StepEquipmentFormEquipment;
